// AddKidSection — parent-facing Settings section for Kids Mode.
// Lists existing kid profiles in the family + a CTA to add a new
// one. Mounted from Settings.jsx; gated to profile_role='parent'.
//
// EMOJI-IN-UI EXCEPTION: this tree (src/experiments/kids-mode/**) is
// the explicit opt-in to emojis per the plan.

import { useEffect, useState } from "react";
import { COLOR, FONT } from "../../lib/tokens";
import { supabase } from "../../lib/supabase";
import { useKidProfiles } from "../../lib/useKidProfiles";
import { createKidProfile } from "../../lib/createKidProfile";
import { petFallbackFor } from "./petSpriteMap";
import { KidMealsManager } from "./KidMealsManager";
import { KidPantryManager } from "./KidPantryManager";
import { CraftImageLab } from "./CraftImageLab";
import { KidRequestsInbox } from "./KidRequestsInbox";
import { useKidRequests } from "../../lib/useKidRequests";
import { SendCardsSheet } from "./SendCards";

const CURRENT_YEAR = new Date().getFullYear();

export function AddKidSection({ userId, onEnterKidMode }) {
  const { kids, loading, refresh } = useKidProfiles(userId);
  const [open, setOpen] = useState(false);
  const [mealsOpen, setMealsOpen] = useState(false);
  const [pantryOpen, setPantryOpen] = useState(false);
  const [labOpen, setLabOpen] = useState(false);
  const [inboxOpen, setInboxOpen] = useState(false);
  const { pending: pendingRequests } = useKidRequests(userId);

  return (
    <div style={{ marginBottom: 18 }}>
      <SectionLabel label="KIDS MODE" />
      {loading && kids.length === 0 ? (
        <div style={{ fontFamily: FONT.forma, fontSize: 12, color: COLOR.muted, padding: "8px 0" }}>
          Loading…
        </div>
      ) : kids.length > 0 ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 12 }}>
          {kids.map((kid) => <KidRow key={kid.id} kid={kid} onEnterKidMode={onEnterKidMode} />)}
        </div>
      ) : null}
      {/* Kid Requests inbox — top priority CTA when anything is
          pending. Pulses with a count badge. */}
      {pendingRequests.length > 0 && (
        <button
          type="button"
          onClick={() => setInboxOpen(true)}
          className="mcm-focusable"
          style={{
            width: "100%",
            padding: 14,
            background: COLOR.leaf,
            color: "#fff",
            border: "none",
            borderRadius: 12,
            fontFamily: FONT.mono,
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: "0.08em",
            cursor: "pointer",
            marginBottom: 8,
            boxShadow: `0 6px 16px ${COLOR.leafDim}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
          }}
        >
          📬 KID REQUESTS
          <span
            style={{
              padding: "2px 10px",
              background: "rgba(255,255,255,0.25)",
              borderRadius: 999,
              fontSize: 11,
            }}
          >
            {pendingRequests.length}
          </span>
        </button>
      )}

      {/* "Manage Kid Meals" lives in this section — it's the data
          that flows TO the kids' app, so it belongs adjacent to
          their roster. Shown even when no kid is added yet so
          adults can pre-stage meals. */}
      <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="mcm-focusable"
          style={{
            flex: 1,
            padding: "14px",
            background: COLOR.skyDeep,
            color: COLOR.sky,
            border: `1px solid ${COLOR.skyBorder}`,
            borderRadius: 12,
            fontFamily: FONT.mono,
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: "0.08em",
            cursor: "pointer",
          }}
        >
          + ADD A KID
        </button>
        <button
          type="button"
          onClick={() => setMealsOpen(true)}
          className="mcm-focusable"
          style={{
            flex: 1,
            padding: "14px",
            background: COLOR.leafDeep,
            color: COLOR.leaf,
            border: `1px solid ${COLOR.leafDim}`,
            borderRadius: 12,
            fontFamily: FONT.mono,
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: "0.08em",
            cursor: "pointer",
          }}
        >
          🍽️ KID MEALS
        </button>
      </div>
      {/* KID ITEMS — sister to KID MEALS but operates on
          pantry_items (scanned products) instead of recipes. Lets
          the parent flag a SPECIFIC item like the Capri Sun in the
          fridge as kid-requestable, with a nickname + Meal/Snack/
          Treat kind. Same flow lands in Foods → Recipes/Treats. */}
      <div style={{ display: "flex", marginBottom: 8 }}>
        <button
          type="button"
          onClick={() => setPantryOpen(true)}
          className="mcm-focusable"
          style={{
            flex: 1,
            padding: "14px",
            background: "linear-gradient(135deg, #9B5DE5 0%, #7A4E2D 100%)",
            color: "#fff",
            border: "none",
            borderRadius: 12,
            fontFamily: FONT.mono,
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: "0.08em",
            cursor: "pointer",
            boxShadow: "0 4px 10px rgba(155, 93, 229, 0.35)",
          }}
        >
          🥫 KID ITEMS (from your kitchen)
        </button>
      </div>
      <div style={{ fontFamily: FONT.sans, fontSize: 11, color: COLOR.muted, marginTop: 8, lineHeight: 1.5 }}>
        Kids get a separate sign-in with a virtual pet, food power-up cards,
        and a watch-along view of what you're cooking. Tap <strong>KID MEALS</strong> to
        pick recipes the kids can request — give each one a kid-friendly
        nickname ("Egg Burgers" for an egg sandwich) and mark it as a
        Meal, Snack, or Treat.
      </div>

      {open && (
        <AddKidSheet
          onClose={() => setOpen(false)}
          onCreated={() => { refresh(); setOpen(false); }}
        />
      )}

      {mealsOpen && (
        <KidMealsManager userId={userId} onClose={() => setMealsOpen(false)} />
      )}

      {pantryOpen && (
        <KidPantryManager userId={userId} onClose={() => setPantryOpen(false)} />
      )}

      <button
        type="button"
        onClick={() => setLabOpen(true)}
        className="mcm-focusable"
        style={{
          width: "100%",
          padding: "12px",
          background: "transparent",
          color: COLOR.muted,
          border: `1px dashed ${COLOR.border}`,
          borderRadius: 10,
          fontFamily: FONT.mono,
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: "0.08em",
          cursor: "pointer",
          marginTop: 8,
        }}
      >
        🧪 RECIPE LAB
      </button>

      {labOpen && <CraftImageLab onClose={() => setLabOpen(false)} />}

      {inboxOpen && (
        <KidRequestsInbox userId={userId} onClose={() => setInboxOpen(false)} />
      )}
    </div>
  );
}

function SectionLabel({ label }) {
  return (
    <div
      style={{
        fontFamily: FONT.mono,
        fontSize: 11,
        letterSpacing: "0.1em",
        fontWeight: 600,
        color: COLOR.sub,
        marginBottom: 10,
        marginTop: 6,
      }}
    >
      {label}
    </div>
  );
}

function KidRow({ kid, onEnterKidMode }) {
  const [pinSet, setPinSet] = useState(null);  // null = loading, bool = known
  const [pinSheet, setPinSheet] = useState(false);
  const [cardsSheet, setCardsSheet] = useState(false);
  const [resetBusy, setResetBusy] = useState(false);
  const [resetToast, setResetToast] = useState(null);

  const resetCraft = async () => {
    if (!confirm(`Wipe ${kid.name || "the kid"}'s craft progress? They start fresh — but the global recipe cache (which all kids share) stays intact.`)) return;
    setResetBusy(true);
    setResetToast(null);
    // NO p_wipe_global_discoveries — only this kid's data clears.
    const { data, error } = await supabase.rpc("reset_kid_craft", {
      p_kid_user_id: kid.id,
    });
    setResetBusy(false);
    if (error) {
      setResetToast({ kind: "err", text: error.message });
    } else {
      setResetToast({ kind: "ok", text: `✓ Reset complete: ${data || "done"}` });
    }
    setTimeout(() => setResetToast(null), 3500);
  };

  // Probe has_kid_exit_pin once on mount per row. Refreshes after
  // a successful set / clear via reload().
  const reloadPinStatus = async () => {
    const { data, error } = await supabase.rpc("has_kid_exit_pin", { p_user_id: kid.id });
    if (error) {
      console.warn("[kid-row] has_kid_exit_pin:", error.message);
      setPinSet(false);
      return;
    }
    setPinSet(data === true);
  };

  useEffect(() => {
    reloadPinStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kid.id]);

  return (
    <>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "10px 12px",
          background: COLOR.ground,
          border: `1px solid ${COLOR.border}`,
          borderRadius: 10,
        }}
      >
        <span style={{ fontSize: 28, lineHeight: 1 }}>🐾</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontFamily: FONT.itemTitle,
              fontStyle: "italic",
              fontSize: 16,
              color: COLOR.ink,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {kid.name || "Kid"}
          </div>
          <div style={{ fontFamily: FONT.forma, fontSize: 12, color: COLOR.muted, display: "flex", gap: 8 }}>
            {kid.birth_year ? <span>Age {Math.max(0, CURRENT_YEAR - kid.birth_year)}</span> : null}
            {pinSet === true && <span>· 🔒 PIN set</span>}
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {/* GIVE CARDS — parent-side shortcut into the SendCardsSheet
              with this kid's id pre-bound. Avoids the previous
              5-step "switch into kid mode → gear → PIN → settings →
              Send Cards tile" trek. Plum gradient ties it visually to
              the same Send-Cards tile in the gear panel + the Magic
              Pen toggle. */}
          <button
            type="button"
            onClick={() => setCardsSheet(true)}
            className="mcm-focusable"
            style={{
              padding: "8px 12px",
              background: "linear-gradient(135deg, #9B5DE5 0%, #7A4E2D 100%)",
              border: "none",
              borderRadius: 8,
              color: "#fff",
              fontFamily: FONT.mono,
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.06em",
              cursor: "pointer",
              whiteSpace: "nowrap",
              boxShadow: "0 4px 10px rgba(155, 93, 229, 0.35)",
              touchAction: "manipulation",
            }}
          >
            🎁 CARDS
          </button>
          {/* ENTER KID MODE — tap to context-switch into this kid's
              session without signing out (kid-as-subset-of-parent
              rework). KidParentPanel's Exit button clears the
              context and returns here. */}
          {onEnterKidMode && (
            <button
              type="button"
              onClick={() => onEnterKidMode(kid.id)}
              className="mcm-focusable"
              style={{
                padding: "8px 12px",
                background: COLOR.leaf,
                border: "none",
                borderRadius: 8,
                color: "#fff",
                fontFamily: FONT.mono,
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.06em",
                cursor: "pointer",
                whiteSpace: "nowrap",
                boxShadow: `0 4px 10px ${COLOR.leafDim}`,
                touchAction: "manipulation",
              }}
            >
              ENTER MODE
            </button>
          )}
          <button
            type="button"
            onClick={() => setPinSheet(true)}
            style={{
              padding: "8px 12px",
              background: pinSet ? COLOR.leafDeep : COLOR.skyDeep,
              border: `1px solid ${pinSet ? COLOR.leafDim : COLOR.skyBorder}`,
              borderRadius: 8,
              color: pinSet ? COLOR.leaf : COLOR.sky,
              fontFamily: FONT.mono,
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: "0.06em",
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            {pinSet ? "PIN" : "SET PIN"}
          </button>
          <button
            type="button"
            onClick={resetCraft}
            disabled={resetBusy}
            title="Wipe craft inventory"
            style={{
              padding: "8px 12px",
              background: "transparent",
              border: `1px solid ${COLOR.roseDim}`,
              borderRadius: 8,
              color: COLOR.rose,
              fontFamily: FONT.mono,
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: "0.06em",
              cursor: resetBusy ? "wait" : "pointer",
              whiteSpace: "nowrap",
              opacity: resetBusy ? 0.6 : 1,
            }}
          >
            🔄 RESET
          </button>
        </div>
      </div>

      {resetToast && (
        <div
          style={{
            marginTop: 6,
            padding: "8px 12px",
            background: resetToast.kind === "ok" ? COLOR.leafDeep : COLOR.roseDeep,
            border: `1px solid ${resetToast.kind === "ok" ? COLOR.leafDim : COLOR.roseDim}`,
            borderRadius: 8,
            color: resetToast.kind === "ok" ? COLOR.leaf : COLOR.rose,
            fontFamily: FONT.forma,
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          {resetToast.text}
        </div>
      )}

      {pinSheet && (
        <SetPinSheet
          kid={kid}
          pinAlreadySet={pinSet === true}
          onClose={() => setPinSheet(false)}
          onChanged={async () => {
            await reloadPinStatus();
            setPinSheet(false);
          }}
        />
      )}

      <SendCardsSheet
        open={cardsSheet}
        kidUserId={kid.id}
        kidName={kid.name || "kid"}
        onClose={() => setCardsSheet(false)}
      />
    </>
  );
}

function SetPinSheet({ kid, pinAlreadySet, onClose, onChanged }) {
  const [pin, setPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const validate = () => {
    if (!/^[0-9]{4,8}$/.test(pin)) return "PIN must be 4–8 digits";
    if (pin !== confirmPin) return "PINs don't match";
    return null;
  };

  const submit = async () => {
    const v = validate();
    if (v) { setError(v); return; }
    setBusy(true);
    setError(null);
    const { error } = await supabase.rpc("set_kid_exit_pin", {
      p_kid_user_id: kid.id,
      p_pin: pin,
    });
    setBusy(false);
    if (error) { setError(error.message); return; }
    onChanged();
  };

  const clear = async () => {
    setBusy(true);
    setError(null);
    const { error } = await supabase.rpc("clear_kid_exit_pin", {
      p_kid_user_id: kid.id,
    });
    setBusy(false);
    if (error) { setError(error.message); return; }
    onChanged();
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(30,30,30,0.45)",
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
        zIndex: 220,
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 420,
          background: "#FAF6EE",
          borderRadius: 20,
          padding: "20px 20px calc(20px + env(safe-area-inset-bottom))",
          boxShadow: "0 -8px 24px rgba(0,0,0,0.18)",
        }}
      >
        <div
          style={{
            fontFamily: FONT.itemTitle,
            fontStyle: "italic",
            fontSize: 22,
            color: COLOR.ink,
            marginBottom: 6,
          }}
        >
          {pinAlreadySet ? "Change Exit PIN" : "Set Exit PIN"}
        </div>
        <div
          style={{
            fontFamily: FONT.forma,
            fontSize: 13,
            color: COLOR.muted,
            marginBottom: 16,
            lineHeight: 1.5,
          }}
        >
          {kid.name || "Kid"} will need this PIN to leave Kids Mode.
          4–8 digits.
        </div>

        <FormFieldInline label="PIN">
          <input
            type="password"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={8}
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/[^0-9]/g, ""))}
            autoFocus
            style={inputPinStyle}
          />
        </FormFieldInline>
        <FormFieldInline label="Confirm">
          <input
            type="password"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={8}
            value={confirmPin}
            onChange={(e) => setConfirmPin(e.target.value.replace(/[^0-9]/g, ""))}
            style={inputPinStyle}
          />
        </FormFieldInline>

        {error && (
          <div
            style={{
              marginBottom: 12,
              padding: "10px 12px",
              background: COLOR.roseDeep,
              border: `1px solid ${COLOR.roseDim}`,
              borderRadius: 8,
              color: COLOR.rose,
              fontFamily: FONT.forma,
              fontSize: 13,
            }}
          >
            {error}
          </div>
        )}

        <div style={{ display: "flex", gap: 10, marginTop: 6 }}>
          {pinAlreadySet && (
            <button
              type="button"
              onClick={clear}
              disabled={busy}
              style={{
                padding: 14,
                background: "transparent",
                color: COLOR.rose,
                border: `1px solid ${COLOR.roseDim}`,
                borderRadius: 10,
                fontFamily: FONT.mono,
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: "0.08em",
                cursor: busy ? "wait" : "pointer",
                opacity: busy ? 0.7 : 1,
              }}
            >
              REMOVE
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            style={{
              flex: 1,
              padding: 14,
              background: "transparent",
              color: COLOR.muted,
              border: `1px solid ${COLOR.border}`,
              borderRadius: 10,
              fontFamily: FONT.mono,
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: "0.08em",
              cursor: "pointer",
            }}
          >
            CANCEL
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={busy || pin.length < 4 || confirmPin.length < 4}
            style={{
              flex: 2,
              padding: 14,
              background: COLOR.leaf,
              color: "#fff",
              border: "none",
              borderRadius: 10,
              fontFamily: FONT.mono,
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: "0.08em",
              cursor: busy ? "wait" : "pointer",
              opacity: busy || pin.length < 4 || confirmPin.length < 4 ? 0.5 : 1,
            }}
          >
            {busy ? "SAVING…" : "SAVE"}
          </button>
        </div>
      </div>
    </div>
  );
}

function FormFieldInline({ label, children }) {
  return (
    <label style={{ display: "block", marginBottom: 12 }}>
      <div
        style={{
          fontFamily: FONT.mono,
          fontSize: 11,
          letterSpacing: "0.1em",
          fontWeight: 600,
          color: COLOR.sub,
          marginBottom: 6,
        }}
      >
        {label.toUpperCase()}
      </div>
      {children}
    </label>
  );
}

const inputPinStyle = {
  width: "100%",
  padding: "14px 16px",
  fontFamily: "inherit",
  fontSize: 22,
  letterSpacing: "0.35em",
  textAlign: "center",
  background: "#fff",
  border: `1px solid ${COLOR.border}`,
  borderRadius: 10,
  color: COLOR.ink,
  boxSizing: "border-box",
};

function AddKidSheet({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [birthYear, setBirthYear] = useState("");
  const [petId, setPetId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    setError(null);
    if (!name.trim()) { setError("name is required"); return; }
    const by = birthYear ? parseInt(birthYear, 10) : null;
    if (by !== null && (Number.isNaN(by) || by < 2000 || by > CURRENT_YEAR)) {
      setError(`birth year should be between 2000 and ${CURRENT_YEAR}`);
      return;
    }
    setBusy(true);
    try {
      await createKidProfile({
        name: name.trim(),
        birthYear: by,
        petId,
      });
      // No magic link — kid has no auth identity. Just close the
      // sheet; the new kid appears in the roster via realtime.
      onCreated();
    } catch (e) {
      setError(e?.message || "could not add kid");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(30,30,30,0.45)",
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
        zIndex: 200,
        padding: "16px",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 480,
          background: "#FAF6EE",
          borderRadius: 20,
          padding: "20px 20px calc(20px + env(safe-area-inset-bottom))",
          boxShadow: "0 -8px 24px rgba(0,0,0,0.18)",
        }}
      >
        {(
          <>
            <div style={{ fontFamily: FONT.itemTitle, fontStyle: "italic", fontSize: 22, color: COLOR.ink, marginBottom: 14 }}>
              Add a Kid
            </div>

            <FormField label="Name">
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Sophie"
                style={inputStyle}
              />
            </FormField>

            <FormField label="Birth year (optional)">
              <input
                type="number"
                inputMode="numeric"
                value={birthYear}
                onChange={(e) => setBirthYear(e.target.value)}
                placeholder="2017"
                min="2000"
                max={String(CURRENT_YEAR)}
                style={inputStyle}
              />
            </FormField>

            <FormField label="Starter pet">
              <PetPicker value={petId} onChange={setPetId} />
            </FormField>

            {error && (
              <div
                style={{
                  marginBottom: 12,
                  padding: "10px 12px",
                  background: COLOR.roseDeep,
                  border: `1px solid ${COLOR.roseDim}`,
                  borderRadius: 8,
                  color: COLOR.rose,
                  fontFamily: FONT.forma,
                  fontSize: 13,
                }}
              >
                {error}
              </div>
            )}

            <div style={{ display: "flex", gap: 10, marginTop: 6 }}>
              <button
                type="button"
                onClick={onClose}
                disabled={busy}
                style={{
                  flex: 1,
                  padding: 14,
                  background: "transparent",
                  color: COLOR.muted,
                  border: `1px solid ${COLOR.border}`,
                  borderRadius: 10,
                  fontFamily: FONT.mono,
                  fontSize: 12,
                  fontWeight: 600,
                  letterSpacing: "0.08em",
                  cursor: "pointer",
                }}
              >
                CANCEL
              </button>
              <button
                type="button"
                onClick={submit}
                disabled={busy}
                style={{
                  flex: 2,
                  padding: 14,
                  background: COLOR.leaf,
                  color: "#fff",
                  border: "none",
                  borderRadius: 10,
                  fontFamily: FONT.mono,
                  fontSize: 12,
                  fontWeight: 600,
                  letterSpacing: "0.08em",
                  cursor: busy ? "wait" : "pointer",
                  opacity: busy ? 0.7 : 1,
                }}
              >
                {busy ? "ADDING…" : "ADD KID"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function FormField({ label, children }) {
  return (
    <label style={{ display: "block", marginBottom: 14 }}>
      <div
        style={{
          fontFamily: FONT.mono,
          fontSize: 11,
          letterSpacing: "0.1em",
          fontWeight: 600,
          color: COLOR.sub,
          marginBottom: 6,
        }}
      >
        {label.toUpperCase()}
      </div>
      {children}
    </label>
  );
}

const inputStyle = {
  width: "100%",
  padding: "12px 14px",
  fontFamily: "inherit",
  fontSize: 16,
  background: "#fff",
  border: `1px solid ${COLOR.border}`,
  borderRadius: 10,
  color: COLOR.ink,
  boxSizing: "border-box",
};

function PetPicker({ value, onChange }) {
  // Data-driven from pet_catalog (migration 0222). Replaces the
  // hardcoded 4-emoji array with whatever's in the catalog —
  // automatically picks up Bob/Evans/Steve/Princess/Peach/Lavender
  // and any future additions without a code change.
  const [pets, setPets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      const { data, error } = await supabase
        .from("pet_catalog")
        .select("id, slug, name, image_url, species")
        .eq("rarity", "common")
        .order("sort_order", { ascending: true });
      if (!alive) return;
      if (error) {
        console.warn("[PetPicker] catalog load failed:", error);
        setPets([]);
      } else {
        setPets(data || []);
      }
      setLoading(false);
    })();
    return () => { alive = false; };
  }, []);

  if (loading) {
    return (
      <div style={{ fontFamily: FONT.mono, fontSize: 12, color: COLOR.muted, padding: 8 }}>
        loading pets…
      </div>
    );
  }

  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(72px, 1fr))", gap: 8 }}>
      {pets.map((pet) => {
        const fb = petFallbackFor(pet.slug);
        const active = value === pet.id;
        return (
          <button
            key={pet.id}
            type="button"
            onClick={() => onChange(pet.id)}
            aria-label={pet.name || pet.slug}
            title={pet.name || pet.slug}
            style={{
              padding: 10,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 4,
              background: active ? COLOR.leafDeep : "#fff",
              border: `2px solid ${active ? COLOR.leaf : COLOR.border}`,
              borderRadius: 10,
              cursor: "pointer",
              touchAction: "manipulation",
            }}
          >
            {pet.image_url
              ? <img src={pet.image_url} alt="" style={{ width: 40, height: 40, objectFit: "contain" }} />
              : <span style={{ fontSize: 32 }}>{fb.emoji}</span>}
            <span style={{ fontFamily: FONT.mono, fontSize: 10, color: active ? "#fff" : COLOR.ink, letterSpacing: "0.05em" }}>
              {(pet.name || pet.slug).toUpperCase()}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function SuccessPanel({ link, onClose }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div>
      <div style={{ fontFamily: FONT.itemTitle, fontStyle: "italic", fontSize: 22, color: COLOR.ink, marginBottom: 8 }}>
        Kid added 🎉
      </div>
      <div style={{ fontFamily: FONT.forma, fontSize: 14, color: COLOR.muted, marginBottom: 16, lineHeight: 1.5 }}>
        Open this link on your kid's device to sign them in.
        It's one-time-use and expires soon.
      </div>
      <div
        style={{
          padding: 12,
          background: "#fff",
          border: `1px solid ${COLOR.border}`,
          borderRadius: 10,
          fontSize: 12,
          fontFamily: FONT.mono,
          wordBreak: "break-all",
          color: COLOR.ink,
          marginBottom: 12,
          maxHeight: 120,
          overflow: "auto",
        }}
      >
        {link || "(no link returned)"}
      </div>
      <div style={{ display: "flex", gap: 10 }}>
        <button
          type="button"
          onClick={copy}
          disabled={!link}
          style={{
            flex: 1,
            padding: 14,
            background: COLOR.skyDeep,
            color: COLOR.sky,
            border: `1px solid ${COLOR.skyBorder}`,
            borderRadius: 10,
            fontFamily: FONT.mono,
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: "0.08em",
            cursor: link ? "pointer" : "not-allowed",
          }}
        >
          {copied ? "COPIED" : "COPY LINK"}
        </button>
        <button
          type="button"
          onClick={onClose}
          style={{
            flex: 1,
            padding: 14,
            background: COLOR.leaf,
            color: "#fff",
            border: "none",
            borderRadius: 10,
            fontFamily: FONT.mono,
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: "0.08em",
            cursor: "pointer",
          }}
        >
          DONE
        </button>
      </div>
    </div>
  );
}

export default AddKidSection;
